import React, { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import { Button, Form, FormGroup, Input, Label } from 'reactstrap';
import { DateTimePicker } from 'react-widgets';
import momentLocalizer from 'react-widgets-moment';
import 'react-widgets/dist/css/react-widgets.css';
import {
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  ExternalLinkIcon,
  useModalControl,
} from '@weave/design-system';
import { WEAVE_APP_URL } from 'apis/sso/sso-url-config';
import { ModalData, CallRecordModal } from './call-record-modal';
import CallRecordModel from '../call-record.model';
import ServerDataTableComponent from '../../../shared/server-data-table/server-data-table.component';
import { InfoTooltip } from '../../../shared/info-tooltip/info-tooltip.component';
import { formatDuration, formatPhoneNumber } from '../../../../helpers/utils';
import { css } from 'emotion';

const flex = css`
  display: flex;
`;
const tooltipMargin = css`
  margin: 5px;
`;
const actionButton = css`
  display: inline-flex;
  align-items: center;
  padding: 4px;
  border: 0;
  background: transparent;
  color: #137c9e;
  cursor: pointer;
`;
const actionLinks = css`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const filters = css`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px 12px;

  .call-record-filter {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin: 0 !important;
  }
  .call-record-filter .datatable-label {
    margin: 0 0 6px;
  }
  .datatable-search-input {
    width: 260px;
    margin: 0;
  }
  .date-range-container {
    width: 180px;
  }
  .date-range-container.with-time {
    width: 210px;
  }
  .call-record-to-input {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .call-record-time-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    white-space: nowrap;
    cursor: pointer;
  }
  .call-record-time-toggle input[type='checkbox'] {
    flex: none;
    width: 16px;
    height: 16px;
    margin: 0;
    padding: 0;
  }
  .datatable-result-select {
    width: 165px;
  }
  .datatable-page-size-select {
    width: 115px;
  }
  .call-record-range-day {
    display: block;
    padding: 4px 0;
    border-radius: 3px;
    background: #cce7f3;
    color: #245e76;
    font-weight: 600;
  }
  .rw-state-selected .call-record-range-day {
    background: transparent;
    color: inherit;
  }
`;
const applyIndicator = css`
  margin-left: 7px;
  opacity: 0.85;
`;
const columnSpacing = { padding: '12px 20px' };
const columnDivider = { ...columnSpacing, borderRight: '1px solid #dce3ea' };
const actionColumnSpacing = { padding: '12px 12px' };
const badge = css`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
  white-space: nowrap;
`;
const badgeTones: Record<string, string> = {
  inbound: css`
    background: #e8f5ef;
    color: #256b55;
  `,
  outbound: css`
    background: #eaf3f8;
    color: #2b607f;
  `,
  answered: css`
    background: #ccefd1;
    color: #276e36;
  `,
  missed: css`
    background: #ffd8d1;
    color: #a4473d;
  `,
  abandoned: css`
    background: #ffe2ad;
    color: #895714;
  `,
  forwarded: css`
    background: #e8d4ef;
    color: #704881;
  `,
};
const neutralBadge = css`
  background: #f2f4f6;
  color: #59646d;
`;
const endOfResults = css`
  padding: 10px 0;
  text-align: center;
  color: #59646d;
  font-size: 14px;
`;
const tableContainer = css`
  overflow-x: auto;

  .ReactTable .rt-table .rt-thead.-header .rt-tr .rt-th,
  .ReactTable .rt-table .rt-tbody .rt-tr-group .rt-tr .rt-td {
    padding: 12px 20px;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ReactTable .rt-table .rt-thead.-header .rt-tr .rt-th {
    background: #f7f9fb;
  }
  .ReactTable .rt-table .rt-thead.-header .rt-tr .rt-th .rt-resizer {
    right: 0;
    width: 12px;
    cursor: col-resize;
    z-index: 2;
  }
  .ReactTable .rt-table .rt-thead.-header .rt-tr .rt-th .rt-resizer:hover {
    background: rgba(72, 106, 137, 0.1);
  }
  .ReactTable .rt-table .rt-thead.-header .rt-tr .rt-th > div:not(.rt-resizer) {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ReactTable .rt-table .rt-tbody .rt-tr-group .rt-tr .rt-td:first-child {
    font-family: monospace;
    font-size: 14px;
  }
  .channel-id {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    max-width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: copy;
  }
  .channel-id:hover .channel-id-text {
    text-decoration: underline;
  }
  .channel-id:focus-visible {
    outline: 2px solid #137c9e;
    outline-offset: 2px;
  }
  .channel-id-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .channel-id-icon {
    flex: none;
    opacity: 0;
  }
  .channel-id:hover .channel-id-icon,
  .channel-id:focus-visible .channel-id-icon {
    opacity: 0.55;
  }
  .channel-id-icon.is-copied {
    opacity: 0.85;
    color: #277b55;
  }
`;

moment.locale('en');
momentLocalizer();

const ChannelIdCell: React.FC<{ value: string }> = ({ value }) => {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');

  useEffect(() => {
    if (copyStatus === 'idle') return;
    const timeout = window.setTimeout(() => setCopyStatus('idle'), 2000);
    return () => window.clearTimeout(timeout);
  }, [copyStatus]);

  const copyChannelId = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('error');
    }
  };

  return (
    <button
      type="button"
      className="channel-id"
      onClick={copyChannelId}
      title={
        copyStatus === 'copied'
          ? 'Copied!'
          : copyStatus === 'error'
          ? 'Could not copy channel ID'
          : `${value} — click to copy`
      }
      aria-label={`Copy channel ID ${value}`}
    >
      <span className="channel-id-text">{value}</span>
      <span
        className={`channel-id-icon${copyStatus === 'copied' ? ' is-copied' : ''}`}
        aria-hidden="true"
      >
        {copyStatus === 'copied' ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
      </span>
      <span className="sr-only" role="status">
        {copyStatus === 'copied' ? 'Channel ID copied' : ''}
      </span>
    </button>
  );
};

interface Props {
  downloadCDR: (channelId: string) => void;
  downloadCDRALeg: (channelId: string) => void;
  getRecords: (
    searchTerm: string,
    start: string,
    end: string,
    limit: number,
    skip: number,
    status: string
  ) => void;
  isNextPageAvailable: boolean; // Not used
  loading: boolean;
  locationId: string;
  records: CallRecordModel[];
  slug: string; // Not used
}

const defaultPageSize = 25;
const channelIdPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const asLocalMoment = (date: Date) => moment(date).local();

export const CallRecords: React.FC<Props> = (props) => {
  const [searchText, setSearchText] = useState('');
  const [status, setStatus] = useState('');
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [appliedPageSize, setAppliedPageSize] = useState(defaultPageSize);
  const [startDate, setStartDate] = useState(
    moment().local().subtract(1, 'week').toDate()
  );
  const modalData = useRef<ModalData>({} as ModalData);
  const { modalProps, openModal, closeModal } = useModalControl();
  const [endDate, setEndDate] = useState(moment(new Date()).toDate());
  const [includeTime, setIncludeTime] = useState(false);
  const defaultDateRange = useRef({ startDate, endDate });
  // Keep edited filter values separate from the values driving the current results.
  const [appliedFilters, setAppliedFilters] = useState({
    searchText: '',
    status: '',
    startDate,
    endDate,
    includeTime: false,
  });
  const csvDateFormat = appliedFilters.includeTime ? 'MM-DD-YYYY-HH-mm' : 'MM-DD-YYYY';
  const [refreshKey, setRefreshKey] = useState(0);
  const previousLocationId = useRef(props.locationId);
  const maxDate = moment().local().add(1, 'day').endOf('day').toDate();
  const invalidDateRange = asLocalMoment(startDate).isAfter(
    asLocalMoment(endDate),
    includeTime ? undefined : 'day'
  );
  const rangeDay = ({ date, label }: { date: Date; label: string }) => (
    <span
      className={
        !invalidDateRange &&
        asLocalMoment(date).isBetween(
          asLocalMoment(startDate),
          asLocalMoment(endDate),
          'day',
          '[]'
        )
          ? 'call-record-range-day'
          : undefined
      }
    >
      {label}
    </span>
  );
  const trimmedSearchText = searchText.trim();
  const normalizedSearchText = channelIdPattern.test(trimmedSearchText)
    ? trimmedSearchText
    : trimmedSearchText.replace(/[\s().-]/g, '');
  const filtersChanged =
    normalizedSearchText !== appliedFilters.searchText ||
    status !== appliedFilters.status ||
    pageSize !== appliedPageSize ||
    includeTime !== appliedFilters.includeTime ||
    !asLocalMoment(startDate).isSame(
      asLocalMoment(appliedFilters.startDate),
      includeTime ? undefined : 'day'
    ) ||
    !asLocalMoment(endDate).isSame(
      asLocalMoment(appliedFilters.endDate),
      includeTime ? undefined : 'day'
    );
  const hasFiltersToClear =
    searchText !== '' ||
    appliedFilters.searchText !== '' ||
    status !== '' ||
    appliedFilters.status !== '' ||
    pageSize !== defaultPageSize ||
    appliedPageSize !== defaultPageSize ||
    includeTime ||
    appliedFilters.includeTime ||
    !asLocalMoment(startDate).isSame(
      asLocalMoment(defaultDateRange.current.startDate),
      'day'
    ) ||
    !asLocalMoment(endDate).isSame(
      asLocalMoment(defaultDateRange.current.endDate),
      'day'
    ) ||
    !asLocalMoment(appliedFilters.startDate).isSame(
      asLocalMoment(defaultDateRange.current.startDate),
      'day'
    ) ||
    !asLocalMoment(appliedFilters.endDate).isSame(
      asLocalMoment(defaultDateRange.current.endDate),
      'day'
    );

  const loadData = useCallback(
    (skip = 0, limit: number = defaultPageSize + 1) => {
      if (props.locationId) {
        props.getRecords(
          appliedFilters.searchText,
          (appliedFilters.includeTime
            ? asLocalMoment(appliedFilters.startDate)
            : asLocalMoment(appliedFilters.startDate).startOf('day')
          ).toISOString(),
          (appliedFilters.includeTime
            ? asLocalMoment(appliedFilters.endDate)
            : asLocalMoment(appliedFilters.endDate).endOf('day')
          ).toISOString(),
          limit,
          skip,
          appliedFilters.status
        );
      }
    },
    [props.getRecords, props.locationId, appliedFilters]
  );

  useEffect(() => {
    if (previousLocationId.current !== props.locationId) {
      previousLocationId.current = props.locationId;
      const nextStartDate = moment().local().subtract(1, 'week').toDate();
      const nextEndDate = new Date();
      defaultDateRange.current = { startDate: nextStartDate, endDate: nextEndDate };
      setSearchText('');
      setStatus('');
      setStartDate(nextStartDate);
      setEndDate(nextEndDate);
      setIncludeTime(false);
      setAppliedFilters({
        searchText: '',
        status: '',
        startDate: nextStartDate,
        endDate: nextEndDate,
        includeTime: false,
      });
      setRefreshKey((key) => key + 1);
    }
  }, [props.locationId]);

  const handleStartDateChange = (date: Date | undefined) => {
    if (date && date <= maxDate) {
      setStartDate(includeTime ? asLocalMoment(date).startOf('minute').toDate() : date);
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (date && date <= maxDate) {
      setEndDate(includeTime ? asLocalMoment(date).endOf('minute').toDate() : date);
    }
  };

  const handleIncludeTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setIncludeTime(enabled);
    if (enabled) {
      // Start with the whole selected days until the user chooses specific times.
      setStartDate(asLocalMoment(startDate).startOf('day').toDate());
      setEndDate(asLocalMoment(endDate).endOf('day').toDate());
    }
  };

  const filter = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (invalidDateRange || props.loading) return;

      setAppliedFilters({
        searchText: normalizedSearchText,
        status,
        startDate,
        endDate,
        includeTime,
      });
      setAppliedPageSize(pageSize);
      setRefreshKey((key) => key + 1);
    },
    [
      normalizedSearchText,
      status,
      startDate,
      endDate,
      includeTime,
      pageSize,
      invalidDateRange,
      props.loading,
    ]
  );

  const clearFilters = () => {
    const nextStartDate = moment().local().subtract(1, 'week').toDate();
    const nextEndDate = new Date();
    defaultDateRange.current = { startDate: nextStartDate, endDate: nextEndDate };
    setSearchText('');
    setStatus('');
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
    setIncludeTime(false);
    setPageSize(defaultPageSize);
    setAppliedPageSize(defaultPageSize);
    setAppliedFilters({
      searchText: '',
      status: '',
      startDate: nextStartDate,
      endDate: nextEndDate,
      includeTime: false,
    });
    setRefreshKey((key) => key + 1);
  };

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  }, []);

  const downloadCDR = (channelId: string) => {
    props.downloadCDR(channelId);
  };

  const downloadCDRALeg = (channelId: string) => {
    props.downloadCDRALeg(channelId);
  };

  const formatCSVData = () => {
    // The final record may be a lookahead row for pagination, not a visible result.
    return props.records.slice(0, appliedPageSize).map((record) => {
      return {
        Direction: record.Direction,
        OfficeNumber:
          record.Direction === 'inbound'
            ? formatPhoneNumber(record.DialedNumber, false)
            : formatPhoneNumber(record.CallerNumber, false),
        PatientNumber:
          record.Direction === 'inbound'
            ? formatPhoneNumber(record.CallerNumber, false)
            : formatPhoneNumber(record.DialedNumber, false),
        DateTime: moment(record.StartedAt).local().format('MM/DD/YYYY h:mm A'),
        Duration: formatDuration(record.Duration),
        Result: record.Status,
      };
    });
  };

  const columns: any[] = [
    {
      Header: 'Channel ID',
      id: 'ChannelID',
      accessor: 'ChannelID',
      width: 310,
      minResizeWidth: 180,
      headerStyle: columnDivider,
      style: columnDivider,
      Cell: ({ value }: { value: string }) => <ChannelIdCell value={value} />,
    },
    {
      Header: 'Direction',
      id: 'direction',
      width: 130,
      headerStyle: columnDivider,
      style: columnDivider,
      accessor: (d: CallRecordModel) => {
        return d.Direction.charAt(0).toUpperCase() + d.Direction.slice(1);
      },
      Cell: ({ value }: { value: string }) =>
        value ? (
          <span className={`${badge} ${badgeTones[value.toLowerCase()] || neutralBadge}`}>
            {value}
          </span>
        ) : (
          '-'
        ),
    },
    {
      Header: 'Office Number',
      id: 'officeNumber',
      width: 185,
      headerStyle: columnDivider,
      style: columnDivider,
      accessor: (d: CallRecordModel) => {
        if (d.Direction === 'inbound') {
          return formatPhoneNumber(d.DialedNumber, false);
        }
        return formatPhoneNumber(d.CallerNumber, false);
      },
    },
    {
      Header: 'Patient Number',
      id: 'patientNumber',
      width: 185,
      headerStyle: columnDivider,
      style: columnDivider,
      accessor: (d: CallRecordModel) => {
        if (d.Direction === 'inbound') {
          return formatPhoneNumber(d.CallerNumber, false);
        }
        return formatPhoneNumber(d.DialedNumber, false);
      },
      Cell: ({ value, original }: { value: string; original: CallRecordModel }) => {
        const patientName = [original.Person?.FirstName, original.Person?.LastName]
          .filter(Boolean)
          .join(' ');
        return <span title={patientName || undefined}>{value}</span>;
      },
    },
    {
      Header: `Call Started Time (${Intl.DateTimeFormat().resolvedOptions().timeZone})`,
      id: 'startedAt',
      width: 320,
      headerStyle: columnDivider,
      style: columnDivider,
      accessor: (d: CallRecordModel) => {
        return moment(d.StartedAt).local().format('MM/DD/YYYY h:mm A');
      },
    },
    {
      Header: 'Duration',
      id: 'duration',
      width: 120,
      headerStyle: columnDivider,
      style: columnDivider,
      accessor: (d: CallRecordModel) => {
        return formatDuration(d.Duration);
      },
    },
    {
      Header: 'Result',
      id: 'status',
      width: 145,
      headerStyle: columnDivider,
      style: columnDivider,
      accessor: (d: CallRecordModel) => {
        return d.Status.charAt(0).toUpperCase() + d.Status.slice(1);
      },
      Cell: ({ value }: { value: string }) =>
        value ? (
          <span className={`${badge} ${badgeTones[value.toLowerCase()] || neutralBadge}`}>
            {value}
          </span>
        ) : (
          '-'
        ),
    },
    {
      Header: 'Actions',
      id: 'actions',
      width: 100,
      headerStyle: actionColumnSpacing,
      style: actionColumnSpacing,
      Cell: ({ row }: { row: ModalData }) => (
        <div className={actionLinks}>
          <button
            type="button"
            className={actionButton}
            title="Download or trace call"
            aria-label={`Download or trace call ${row.ChannelID}`}
            onClick={() => {
              modalData.current = row;
              openModal();
            }}
          >
            <DownloadIcon size={20} />
          </button>
          <a
            className={actionButton}
            href={`${WEAVE_APP_URL}/calls/call-history?linktype=callHistory&channelId=${encodeURIComponent(
              row.ChannelID
            )}&locationId=${encodeURIComponent(props.locationId)}`}
            target="_blank"
            rel="noopener noreferrer"
            title="View call details in Weave"
            aria-label={`View call details in Weave for ${row.ChannelID}`}
          >
            <ExternalLinkIcon size={20} />
          </a>
        </div>
      ),
    },
  ];

  return (
    <div className="container-fluid">
      <div className="main">
        <div className="title-group">
          <div className={flex}>
            <div className="page-title">Call Records</div>
            <InfoTooltip
              className={tooltipMargin}
              body={
                <div>
                  Use Actions to download or trace a call, or view its details in Weave.
                </div>
              }
              tooltipPlacement="right"
            />
          </div>
        </div>
        <div className="datatable-library-container">
          <Form className={`datatable-search-container ${filters}`} onSubmit={filter}>
            <FormGroup className="call-record-filter">
              <Label for="search" className="datatable-label">
                Search
              </Label>
              <Input
                type="text"
                className="datatable-search-input"
                name="search"
                id="search"
                value={searchText}
                onChange={handleSearchChange}
                placeholder="Phone number or channel ID"
              />
            </FormGroup>
            <FormGroup className="call-record-filter">
              <Label for="call-result" className="datatable-label">
                Result
              </Label>
              <Input
                type="select"
                className="datatable-result-select"
                name="call-result"
                id="call-result"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="">All results</option>
                <option value="answered">Answered</option>
                <option value="missed">Missed</option>
                <option value="abandoned">Abandoned</option>
                <option value="forwarded">Forwarded</option>
              </Input>
            </FormGroup>
            <FormGroup className="call-record-filter">
              <Label className="datatable-label">From</Label>
              <div className={`date-range-container${includeTime ? ' with-time' : ''}`}>
                <DateTimePicker
                  aria-label={includeTime ? 'Start date and time' : 'Start date'}
                  format={includeTime ? 'MM/DD/YYYY h:mm A' : 'MM/DD/YYYY'}
                  time={includeTime}
                  value={startDate}
                  max={maxDate}
                  dayComponent={rangeDay}
                  onChange={handleStartDateChange}
                />
              </div>
            </FormGroup>
            <FormGroup className="call-record-filter">
              <Label className="datatable-label">To</Label>
              <div className="call-record-to-input">
                <div className={`date-range-container${includeTime ? ' with-time' : ''}`}>
                  <DateTimePicker
                    aria-label={includeTime ? 'End date and time' : 'End date'}
                    format={includeTime ? 'MM/DD/YYYY h:mm A' : 'MM/DD/YYYY'}
                    time={includeTime}
                    value={endDate}
                    max={maxDate}
                    dayComponent={rangeDay}
                    onChange={handleEndDateChange}
                  />
                </div>
                <label className="call-record-time-toggle" htmlFor="call-include-time">
                  <input
                    type="checkbox"
                    id="call-include-time"
                    aria-label="Include time in date range"
                    checked={includeTime}
                    onChange={handleIncludeTimeChange}
                  />
                  <span>Time</span>
                </label>
              </div>
            </FormGroup>
            <FormGroup className="call-record-filter">
              <Label for="call-page-size" className="datatable-label">
                Page size
              </Label>
              <Input
                type="select"
                id="call-page-size"
                className="datatable-page-size-select"
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
              >
                {[10, 25, 50, 75, 100, 150, 200].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <Button
              type="submit"
              size="sm"
              color={filtersChanged && !invalidDateRange ? 'info' : 'secondary'}
              disabled={invalidDateRange || props.loading}
            >
              {filtersChanged ? 'Apply filters' : 'Refresh'}
              {filtersChanged && !invalidDateRange && (
                <span className={applyIndicator} aria-hidden="true">
                  →
                </span>
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              color={hasFiltersToClear ? 'info' : 'secondary'}
              disabled={!hasFiltersToClear}
              onClick={clearFilters}
            >
              Clear filters
            </Button>
            {invalidDateRange && (
              <span role="alert" className="text-danger">
                From {includeTime ? 'date and time' : 'date'} must be on or before To{' '}
                {includeTime ? 'date and time' : 'date'}.
              </span>
            )}
            <CSVLink
              className="btn btn-info btn-sm"
              filename={`${asLocalMoment(appliedFilters.startDate).format(
                csvDateFormat
              )}-${asLocalMoment(appliedFilters.endDate).format(
                csvDateFormat
              )}-call-records.csv`}
              data={formatCSVData()}
            >
              Export
            </CSVLink>
          </Form>
          <CallRecordModal
            closeModal={closeModal}
            modalData={modalData.current}
            canPerformActions={true}
            downloadCDR={downloadCDR}
            downloadCDRALeg={downloadCDRALeg}
            {...modalProps}
          />
          <div className={tableContainer}>
            <ServerDataTableComponent
              columns={columns}
              data={props.records}
              loading={props.loading}
              sortable={false}
              resizable={true}
              loadData={loadData}
              resetPage={false}
              refreshKey={refreshKey}
              pageSize={appliedPageSize}
              minRows={0}
              showPageSizeOptions={false}
            />
            {!props.loading &&
              props.records.length > 0 &&
              props.records.length <= appliedPageSize && (
                <div className={endOfResults} role="status">
                  You've reached the end of the results for the applied filters.
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};
